import { useMemo } from 'react'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import { useTaskLabels } from '@/lib/db/rxdb/hooks'
import { useAllLabels } from '@/lib/hooks/useLabels'

/**
 * Returns the N most recently used labels for the given workspace, ranked
 * by the latest `updatedOn` across non-deleted taskLabel rows.
 *
 * Labels that have never been attached to any task are excluded — this is a
 * "recents" surface, not a full label list.
 */
export function useRecentlyUsedLabels(
  workspaceId: string | null | undefined,
  limit = 10,
): { labels: Array<LabelDocument>; isLoading: boolean } {
  const { labels, isLoading: labelsLoading } = useAllLabels(workspaceId)
  const { taskLabels, isLoading: taskLabelsLoading } = useTaskLabels(
    workspaceId ? { envId: workspaceId, isDeleted: false } : undefined,
  )

  const ranked = useMemo<Array<LabelDocument>>(() => {
    if (!workspaceId || labels.length === 0 || taskLabels.length === 0) {
      return []
    }

    const lastUsed = new Map<string, number>()
    for (const tl of taskLabels) {
      if (tl.isDeleted) continue
      const prev = lastUsed.get(tl.labelId) ?? -Infinity
      if (tl.updatedOn > prev) {
        lastUsed.set(tl.labelId, tl.updatedOn)
      }
    }

    return labels
      .filter((l) => !l.isDeleted && lastUsed.has(l.id))
      .map((l) => ({ label: l, lastUsedAt: lastUsed.get(l.id) ?? 0 }))
      .sort((a, b) => b.lastUsedAt - a.lastUsedAt)
      .slice(0, limit)
      .map((r) => r.label)
  }, [workspaceId, labels, taskLabels, limit])

  return {
    labels: ranked,
    isLoading: labelsLoading || taskLabelsLoading,
  }
}
